import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';

/**
 * Promise-based confirmation for destructive actions.
 *
 *   const confirm = useConfirm();
 *   if (!await confirm({ title, description })) return;
 *
 * Pass `phrase` to require the user to type it out — reserved for actions that
 * destroy other people's data, where an accidental click is unrecoverable.
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useI18n();
  const [opts, setOpts] = useState(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolver.current = resolve;
    setTyped('');
    setOpts(options);
  }), []);

  const settle = useCallback((accepted) => {
    const resolve = resolver.current;
    resolver.current = null;
    setOpts(null);
    setTyped('');
    resolve?.(accepted);
  }, []);

  const phrase = opts?.phrase;
  const ready = !phrase || typed.trim() === phrase;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!opts} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{opts?.title}</AlertDialogTitle>
            {opts?.description && (
              <AlertDialogDescription>{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>

          {phrase && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('confirm.typeToConfirm', { phrase })}
              </p>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                aria-label={t('confirm.typeToConfirm', { phrase })}
                data-testid="confirm-phrase-input"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!ready}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
              data-testid="confirm-accept"
              onClick={(e) => {
                if (!ready) { e.preventDefault(); return; }
                settle(true);
              }}
            >
              {opts?.actionLabel || t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  // Falling back to "declined" is the safe default: if the provider is missing,
  // a destructive action must not silently proceed.
  return useContext(ConfirmContext) || (async () => false);
}
